/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import moment from 'moment';
import {
  ICarbonEmissiondetails,
  ICarbonMeters
} from '../../DashboardKPIWidgets/CarbonEmissionKPI/CarbonEmissionHOC/ICarbonEmissionHOCProps';
import {
  ECartAt,
  IEmissionsByUtility,
  IMeter,
  IFuelType,
  IMeterSavingsData,
  IFuelTypeSavingsData,
  IMonthWise,
  IMonthwiseStackedChartData,
  ISite,
  ISiteSavingsData,
  IMonthwiseStackedChartDataEnergy,
  IBasicMonthWise,
  EnergyDisaggregation
} from '../../DashboardKPIWidgets/IEnergyManagementProps';
import { EmaasSubscriptionType } from '../Enums';
import {
  colorsAvailableForFuels,
  FuelColorMapping,
  FuelColorMappingFor6,
  KPI_PERCENTAGE_DECIMAL_PRECISION,
  PERCENT_CHANGE_THRESHOLD,
  StackedCarbonScopeOneSubUtilities,
  UtilityType
} from './Constants';
import {
  IMeterDataForCarbonTrendChart,
  ITrendMetersData
} from './ICarbonViewDetailsUtils';
import { fuelTypes } from './types';
import {
  getCarbonTotalEmissionValues,
  getCarbonUtilitySavingsValue
} from './unitHandling/EmaasSiteKpiDataScaling';
import {
  getConvertedValue,
  getMaxConvertedValueFromArray
} from './unitHandling/unitHandling';
import { ConversionType } from './unitHandling/UnitModel';
import { excludeCurrentMonth, formattedDate, graphValue } from './Utils';

const compareMonth = (month1: string, month2: string) => {
  const cmp1 = new Date(month1).getMonth();
  const cmp2 = new Date(month2).getMonth();
  return cmp1 === cmp2 ? true : false;
};

export const mergeMonthAggregations = (
  emissionAggregations: IMonthWise[],
  avoidanceAggregations: IMonthWise[]
) => {
  emissionAggregations?.forEach((EA) => {
    const avoidanceData = avoidanceAggregations.find(
      (x) =>
        new Date(x.monthStartDate).getMonth() ===
        new Date(EA.monthStartDate).getMonth()
    );
    EA.totalActualAvoidance = avoidanceData?.totalActualAvoidance ?? 0;
    EA.totalPreviousPeriodAvoidance =
      avoidanceData?.totalPreviousPeriodAvoidance ?? 0;
  });
};

export const convertDataForCarbonTrendChart = (
  monthwiseAggregations: Array<IMonthWise>,
  fromUnit: string,
  totalSiteCount?: any,
  lng?: string
) => {
  let value = graphValue(monthwiseAggregations, lng);
  const valuesArray: number[] = [];
  monthwiseAggregations?.forEach(
    ({
      totalActualEmission,
      totalPreviousPeriodEmission,
      totalActualAvoidance,
      totalPreviousPeriodAvoidance
    }) => {
      valuesArray.push(
        totalActualEmission,
        totalPreviousPeriodEmission,
        totalActualAvoidance,
        totalPreviousPeriodAvoidance
      );
    }
  );
  const toUnit = getMaxConvertedValueFromArray(
    ConversionType.CARBON,
    fromUnit,
    valuesArray
  ).Unit;
  let IsCompleted: Array<boolean> = [];
  let PeriodUpto: Array<number> = [];
  let baselineConsumptionData: Array<number> = [];
  let actualConsumptionData: Array<number> = [];
  let actualAvoidanceData: Array<number> = [];
  let previousAvoidanceData: Array<number> = [];
  let YearIndex: Array<string> = [];
  let MonthIndex: Array<string> = [];
  YearIndex = value.yearIndex;
  let SiteWithDataCount: any = [];
  MonthIndex = value.monthIndex;

  monthwiseAggregations?.forEach(
    ({
      totalActualEmission,
      totalPreviousPeriodEmission,
      isCompleted,
      emissionProcessedDays,
      totalActualAvoidance,
      totalPreviousPeriodAvoidance,
      sitesWithData
    }) => {
      IsCompleted.push(isCompleted);
      PeriodUpto.push(emissionProcessedDays);
      const unitScaledPreviousPeriod = getConvertedValue(
        ConversionType.CARBON,
        fromUnit,
        totalPreviousPeriodEmission,
        toUnit
      ).Value;
      baselineConsumptionData.push(unitScaledPreviousPeriod ?? 0);
      const unitScaledActual = getConvertedValue(
        ConversionType.CARBON,
        fromUnit,
        totalActualEmission,
        toUnit
      ).Value;
      actualConsumptionData.push(unitScaledActual ?? 0);
      const unitScaledActualAvoidance = getConvertedValue(
        ConversionType.CARBON,
        fromUnit,
        totalActualAvoidance,
        toUnit
      ).Value;
      actualAvoidanceData.push(unitScaledActualAvoidance ?? 0);
      const unitScaledPreviousAvoidance = getConvertedValue(
        ConversionType.CARBON,
        fromUnit,
        totalPreviousPeriodAvoidance,
        toUnit
      ).Value;
      previousAvoidanceData.push(unitScaledPreviousAvoidance ?? 0);
      SiteWithDataCount.push(sitesWithData);
    }
  );
  return {
    baselineConsumptionData,
    actualConsumptionData,
    actualAvoidanceData,
    previousAvoidanceData,
    MonthIndex,
    YearIndex,
    IsCompleted,
    PeriodUpto,
    toUnit,
    totalSiteCount,
    SiteWithDataCount
  };
};

export const getMonthwiseDataForFual = (
  fuelType: string,
  monthwiseAggregations: Array<IMonthwiseStackedChartData>,
  scaledUnit: (value: number) => number | null
) => {
  let actual: (number | null)[] = [],
    previous: (number | null)[] = [];
  monthwiseAggregations?.forEach((monthlyData) => {
    let isMonthDataAvailable = false;
    monthlyData.emissionsByFuelType?.forEach((subUtility) => {
      if (subUtility.fuelTypeName === fuelType) {
        isMonthDataAvailable = true;
        actual.push(scaledUnit(subUtility.totalActualEmission!));
        previous.push(scaledUnit(subUtility.totalPreviousPeriodEmission!));
      }
    });
    if (!isMonthDataAvailable) {
      actual.push(null);
      previous.push(null);
    }
  });
  return { actual, previous };
};

export const getMonthwiseDataForElectricity = (
  fuelType: string,
  monthwiseAggregations: Array<IBasicMonthWise>,
  scaledUnit: (value: number) => number | null
) => {
  let actual: (number | null)[] = [],
    previous: (number | null)[] = [];
  monthwiseAggregations?.forEach((monthlyData) => {
    let isMonthDataAvailable = false;
    monthlyData.disaggregation?.forEach((subUtility) => {
      if (subUtility.meterName === fuelType) {
        isMonthDataAvailable = true;
        actual.push(scaledUnit(subUtility.totalConsumption!));
        previous.push(scaledUnit(subUtility.totalBaselineConsumption!));
      }
    });
    if (!isMonthDataAvailable) {
      actual.push(null);
      previous.push(null);
    }
  });
  return { actual, previous };
};

export const getMonthwiseDataForEnergyDisaggregation = (
  fuelType: string,
  energyDisaggregation: Array<EnergyDisaggregation>,
  scaledUnit: (value: number) => number | null
) => {
  let actual: (number | null)[] = [],
    previous: (number | null)[] = [];
  energyDisaggregation?.forEach((monthlyData) => {
    let isMonthDataAvailable = false;
    monthlyData.disaggregation?.forEach((subUtility) => {
      if (subUtility.name === fuelType) {
        isMonthDataAvailable = true;
        actual.push(scaledUnit(subUtility.totalConsumption!));
        previous.push(scaledUnit(subUtility.baselineConsumption!));
      }
    });
    if (fuelType === 'TotalConsuption') {
      let totalConsumtpyion = monthlyData.disaggregation.reduce(
        (prev, curr) => prev + (curr?.totalConsumption || 0),
        0
      );
      isMonthDataAvailable = true;
      actual.push(scaledUnit(totalConsumtpyion!));
      previous.push(scaledUnit(totalConsumtpyion!));
    }
    if (!isMonthDataAvailable) {
      actual.push(null);
      previous.push(null);
    }
  });
  return { actual, previous };
};

export const convertStackedDataForCarbonTrendChart = (
  monthwiseAggregations: Array<IMonthwiseStackedChartData>,
  fromUnit: string,
  totalSiteCount?: number,
  lng?: string
) => {
  let value = graphValue(monthwiseAggregations, lng);
  const maxValuesArray: number[] = [];
  monthwiseAggregations?.forEach((monthData) => {
    maxValuesArray.push(
      monthData.totalActualEmission,
      monthData.totalPreviousPeriodEmission
    );
  });
  const maxValueUnitConversion = getMaxConvertedValueFromArray(
    ConversionType.CARBON,
    fromUnit,
    maxValuesArray
  );

  const scaledUnit = (value: number) =>
    getConvertedValue(
      ConversionType.CARBON,
      fromUnit,
      value,
      maxValueUnitConversion.Unit
    ).Value;
  let YearIndex: Array<string> = [];
  let MonthIndex: Array<string> = [];
  YearIndex = value.yearIndex;
  MonthIndex = value.monthIndex;
  let SiteWithDataCount: any = [];

  let uniqueFuelTypes: string[] = [];
  monthwiseAggregations?.forEach((monthData) => {
    SiteWithDataCount.push(monthData.sitesWithData);
    monthData.emissionsByFuelType?.forEach((subUtilityEmission) => {
      if (!uniqueFuelTypes.includes(subUtilityEmission.fuelTypeName))
        uniqueFuelTypes.push(subUtilityEmission.fuelTypeName);
    });
  });

  const fuel = uniqueFuelTypes.map((fuel, index) => {
    const { actual, previous } = getMonthwiseDataForFual(
      fuel,
      monthwiseAggregations,
      scaledUnit
    );
    return {
      name: fuel,
      color:
        uniqueFuelTypes.length <= 6
          ? FuelColorMappingFor6[index]
          : FuelColorMapping[getColorIndex(index)],
      actual,
      previous
    };
  });

  return {
    MonthIndex,
    YearIndex,
    fuel,
    unit: maxValueUnitConversion.Unit,
    SiteWithDataCount,
    totalSiteCount
  };
};

export const getColorIndex = (index: number): number => {
  if (index < colorsAvailableForFuels) {
    return index;
  } else {
    return getColorIndex(index - colorsAvailableForFuels);
  }
};

export const convertDataForCarbonDetailSavingsMeter = (
  emissionsByUtility: Array<IEmissionsByUtility>,
  fromUnit: string,
  showChangeWhenData: boolean = false
) => {
  const meterSavingsData: Array<IMeterDataForCarbonTrendChart> = [];

  const valuesArray = getCarbonTotalEmissionValues(emissionsByUtility);
  const maxValueUnitConversion = getMaxConvertedValueFromArray(
    ConversionType.CARBON,
    fromUnit,
    valuesArray
  );
  emissionsByUtility?.forEach((data) => {
    const {
      totalActualEmission,
      totalPreviousPeriodEmission,
      totalDeviation,
      totalDeviationPercentage,
      utilityType
    } = data;
    const hasBaseline =
      showChangeWhenData &&
      (isNaN(Number(totalPreviousPeriodEmission)) ||
        isNaN(Number(totalActualEmission)) ||
        Number(totalPreviousPeriodEmission) === 0 ||
        Number(totalActualEmission) === 0 ||
        Math.abs(Number(totalDeviationPercentage)) > PERCENT_CHANGE_THRESHOLD);

    let meterSavings = {
      totalActualEmission: totalActualEmission,
      totalPreviousPeriodEmission: totalPreviousPeriodEmission,
      totalDeviation: hasBaseline ? null : totalDeviation,
      percentageEmissionsSaving: totalDeviationPercentage,
      utilityType,
      actualUnit: getMaxConvertedValueFromArray(
        ConversionType.CARBON,
        fromUnit,
        [totalActualEmission]
      ).Unit,
      baselineUnit: getMaxConvertedValueFromArray(
        ConversionType.CARBON,
        fromUnit,
        [totalPreviousPeriodEmission]
      ).Unit,
      deviationUnit: getMaxConvertedValueFromArray(
        ConversionType.CARBON,
        fromUnit,
        [totalDeviation]
      ).Unit,
      chnageUnit: getMaxConvertedValueFromArray(
        ConversionType.CARBON,
        fromUnit,
        [totalDeviation]
      ).Unit
    };
    meterSavingsData.push(meterSavings as IMeterDataForCarbonTrendChart);
  });
  return { values: meterSavingsData, displayUnit: maxValueUnitConversion.Unit };
};

export const getUtilityTypeMeterCarbonDetails = (
  breakdownByMeter: Array<IMeter> | undefined,
  fromUnit: string,
  toUnit: string,
  showChangeWhenData: boolean = false
) => {
  const electricityMeters: Array<ITrendMetersData> = [];
  const gasMeters: Array<ITrendMetersData> = [];

  breakdownByMeter?.forEach((data: IMeter) => {
    const {
      utilityType,
      meterId,
      meterName,
      totalActualEmission,
      totalPreviousPeriodEmission,
      totalDeviation,
      totalDeviationPercentage
    } = data;
    const hasBaseline =
      showChangeWhenData &&
      (isNaN(Number(totalPreviousPeriodEmission)) ||
        isNaN(Number(totalActualEmission)) ||
        Number(totalPreviousPeriodEmission) === 0 ||
        Number(totalActualEmission) === 0 ||
        Math.abs(Number(totalDeviationPercentage)) > PERCENT_CHANGE_THRESHOLD);

    if (
      utilityType
        .toLowerCase()
        .includes(UtilityType.Electricity.toLowerCase()) ||
      utilityType.toLowerCase().includes(UtilityType.Electric)
    ) {
      let meterSavings: ITrendMetersData = {
        meterId: meterId,
        meterName: meterName,
        utilityType: utilityType,
        totalActualEmission: totalActualEmission,
        totalPreviousPeriodEmission: totalPreviousPeriodEmission,
        totalDeviation: hasBaseline ? null : totalDeviation,
        totalDeviationPercentage: totalDeviationPercentage,
        actualUnit: getMaxConvertedValueFromArray(
          ConversionType.CARBON,
          fromUnit,
          [totalActualEmission]
        ).Unit,
        changeUnit: getMaxConvertedValueFromArray(
          ConversionType.CARBON,
          fromUnit,
          [totalDeviation]
        ).Unit,
        deviationUnit: getMaxConvertedValueFromArray(
          ConversionType.CARBON,
          fromUnit,
          [totalDeviation]
        ).Unit,
        baselineUnit: getMaxConvertedValueFromArray(
          ConversionType.CARBON,
          fromUnit,
          [totalPreviousPeriodEmission]
        ).Unit
      };
      electricityMeters.push(meterSavings);
    }

    if (utilityType.toLowerCase().includes(UtilityType.Gas.toLowerCase())) {
      let meterSavings: ITrendMetersData = {
        meterId: meterId,
        meterName: meterName,
        utilityType: utilityType,
        totalActualEmission: totalActualEmission,
        totalPreviousPeriodEmission: totalPreviousPeriodEmission,
        totalDeviation: hasBaseline ? null : totalDeviation,
        totalDeviationPercentage: totalDeviationPercentage,
        actualUnit: getMaxConvertedValueFromArray(
          ConversionType.CARBON,
          fromUnit,
          [totalActualEmission]
        ).Unit,
        changeUnit: getMaxConvertedValueFromArray(
          ConversionType.CARBON,
          fromUnit,
          [totalDeviation]
        ).Unit,
        deviationUnit: getMaxConvertedValueFromArray(
          ConversionType.CARBON,
          fromUnit,
          [totalDeviation]
        ).Unit,
        baselineUnit: getMaxConvertedValueFromArray(
          ConversionType.CARBON,
          fromUnit,
          [totalPreviousPeriodEmission]
        ).Unit
      };
      gasMeters.push(meterSavings);
    }
  });
  return { electricityMeters, gasMeters };
};

export const getCarbonTotalEmissionMeterDetails = (
  utilityMeterDetails: Array<ICarbonEmissiondetails>
) => {
  let utilityMeterDetail: Array<ICarbonMeters> = [];
  utilityMeterDetails?.forEach((data) => {
    const { meterSavingdata, utilityTypeMeterDetails } = data;

    for (const element of meterSavingdata) {
      if (
        element.utilityType.toLowerCase() ===
          UtilityType.Electricity.toLowerCase() ||
        element.utilityType.toLowerCase() === UtilityType.Electric.toLowerCase()
      ) {
        let electricityData: ICarbonMeters = {
          totalActualEmission: element.totalActualEmission,
          totalPreviousPeriodEmission: element.totalPreviousPeriodEmission,
          percentageEmissionsSaving: parseFloat(
            element.percentageEmissionsSaving.toFixed(
              KPI_PERCENTAGE_DECIMAL_PRECISION
            )
          ),
          totalDeviation: element.totalDeviation,
          utilityType: element.utilityType,
          utilityMeters: utilityTypeMeterDetails.electricityMeters,
          actualUnit: element.actualUnit,
          baselineUnit: element.baselineUnit,
          changeUnit: element.changeUnit,
          deviationUnit: element.deviationUnit
        };
        utilityMeterDetail.push(electricityData);
      }
      if (element.utilityType.toLowerCase() === UtilityType.Gas.toLowerCase()) {
        let gasData: ICarbonMeters = {
          totalActualEmission: element.totalActualEmission,
          totalPreviousPeriodEmission: element.totalPreviousPeriodEmission,
          percentageEmissionsSaving: parseFloat(
            element.percentageEmissionsSaving.toFixed(
              KPI_PERCENTAGE_DECIMAL_PRECISION
            )
          ),
          totalDeviation: element.totalDeviation,
          utilityType: element.utilityType,
          utilityMeters: utilityTypeMeterDetails.gasMeters,
          actualUnit: element.actualUnit,
          baselineUnit: element.baselineUnit,
          changeUnit: element.changeUnit,
          deviationUnit: element.deviationUnit
        };
        utilityMeterDetail.push(gasData);
      }
    }
  });
  let maxConsup = Number.MIN_VALUE;
  utilityMeterDetail?.forEach((meter: ICarbonMeters) => {
    let tempMax =
      meter.totalActualEmission > meter.totalPreviousPeriodEmission
        ? meter.totalActualEmission
        : meter.totalPreviousPeriodEmission;
    maxConsup = maxConsup > tempMax ? maxConsup : tempMax;
  });
  utilityMeterDetail = utilityMeterDetail.map((meterDetails) => {
    return {
      ...meterDetails,
      progessBarRelativeWidth: maxConsup,
      utilityMeters: meterDetails.utilityMeters.map(
        (meter: ITrendMetersData) => {
          return {
            ...meter,
            progessBarRelativeWidth: maxConsup
          };
        }
      )
    };
  });
  return utilityMeterDetail;
};

export const convertDataForCarbonUtilitySavingsMeter = (
  breakdownByMeter: Array<IMeter>,
  fromUnit: string,
  conversionType: string,
  showChangeWhenData: boolean = false
) => {
  let meterSavingsData: Array<IMeterSavingsData> = [];

  const valuesArray = getCarbonUtilitySavingsValue(breakdownByMeter);
  const maxValueUnitConversion = getMaxConvertedValueFromArray(
    ConversionType.CARBON,
    fromUnit,
    valuesArray
  );

  breakdownByMeter?.forEach((data) => {
    const {
      meterName,
      utilityType,
      totalActualEmission,
      totalPreviousPeriodEmission,
      totalDeviation,
      totalDeviationPercentage
    } = data;

    const hasBaseline =
      showChangeWhenData &&
      (isNaN(Number(totalPreviousPeriodEmission)) ||
        isNaN(Number(totalActualEmission)) ||
        Number(totalPreviousPeriodEmission) === 0 ||
        Number(totalActualEmission) === 0 ||
        Math.abs(Number(totalDeviationPercentage)) > PERCENT_CHANGE_THRESHOLD);

    let meterSavings: IMeterSavingsData = {
      meter: meterName,
      actualConsumption: totalActualEmission,
      baselineConsumption: totalPreviousPeriodEmission,
      change: hasBaseline ? null : totalDeviation,
      actualUnit: getMaxConvertedValueFromArray(
        ConversionType.CARBON,
        fromUnit,
        [totalActualEmission]
      ).Unit,
      changeUnit: getMaxConvertedValueFromArray(
        ConversionType.CARBON,
        fromUnit,
        [totalDeviation]
      ).Unit,
      baselineUnit: getMaxConvertedValueFromArray(
        ConversionType.CARBON,
        fromUnit,
        [totalPreviousPeriodEmission]
      ).Unit,
      deviationUnit: getMaxConvertedValueFromArray(
        ConversionType.CARBON,
        fromUnit,
        [totalDeviation]
      ).Unit,
      percentageConsumptionSaving: parseFloat(
        totalDeviationPercentage.toFixed(KPI_PERCENTAGE_DECIMAL_PRECISION)
      ),
      utilityType: utilityType
    };
    meterSavingsData.push(meterSavings);
  });

  let maxRelativeValue: number | null = Number.MIN_VALUE;
  meterSavingsData?.forEach((meter: IMeterSavingsData) => {
    const tempMax =
      meter.actualConsumption! > meter.baselineConsumption!
        ? meter.actualConsumption
        : meter.baselineConsumption;
    maxRelativeValue =
      tempMax! > maxRelativeValue! ? tempMax : maxRelativeValue;
  });
  meterSavingsData = meterSavingsData.map((meterdata: IMeterSavingsData) => {
    return {
      ...meterdata,
      progessBarRelativeWidth: maxRelativeValue
    };
  });
  return { values: meterSavingsData, displayUnit: maxValueUnitConversion.Unit };
};

export const flattenEmissionByScopeArray = (
  arr: Array<IFuelType> | undefined,
  flat: Array<IFuelType>
) => {
  arr &&
    arr?.forEach((ar: IFuelType) => {
      flat.push(ar);
      if (ar.emissionsBySubScope && ar.emissionsBySubScope.length > 0) {
        flattenEmissionByScopeArray(ar.emissionsBySubScope, flat);
      } else if (ar.emissionsByFuelType && ar.emissionsByFuelType.length > 0) {
        flattenEmissionByScopeArray(ar.emissionsByFuelType, flat);
      } else {
        console.log('');
      }
    });
  return flat;
};

export const convertDataForCarbonUtilityScopeOneSavingsMeter = (
  fromUnit: string,
  conversionType: string,
  breakdownByMeter: Array<IFuelType> = [],
  flattenedEmissionBySubScope: Array<IFuelType> = [],
  showChangeWhenData: boolean = false
) => {
  let meterSavingsData: Array<IFuelTypeSavingsData> = [];

  const valuesArray = getCarbonUtilitySavingsValue(
    flattenedEmissionBySubScope && flattenedEmissionBySubScope.length > 0
      ? flattenedEmissionBySubScope
      : breakdownByMeter
  );
  const maxValueUnitConversion = getMaxConvertedValueFromArray(
    ConversionType.CARBON,
    fromUnit,
    valuesArray
  );

  breakdownByMeter?.forEach((data) => {
    const {
      emissionsBySubScope,
      scope,
      // meterName,
      combustionType,
      totalActualEmission,
      totalPreviousPeriodEmission,
      totalDeviation,
      totalDeviationPercentage,
      emissionsByFuelType,
      fuelTypeName,
      isLive,
      fuelTypeId
    } = data;

    const hasBaseline =
      showChangeWhenData &&
      (isNaN(Number(totalPreviousPeriodEmission)) ||
        isNaN(Number(totalActualEmission)) ||
        Number(totalPreviousPeriodEmission) === 0 ||
        Number(totalActualEmission) === 0 ||
        Math.abs(Number(totalDeviationPercentage)) > PERCENT_CHANGE_THRESHOLD);

    let meterSavings: IFuelTypeSavingsData = {
      // childrenMeters: convertDataForCarbonUtilitySavingsMeterForChildren(children,fromUnit, valuesArray),
      // meter: meterName,
      fuelTypeName,
      isLive,
      fuelTypeId,
      emissionsBySubScope: emissionsBySubScope
        ? convertDataForCarbonUtilityScopeOneSavingsMeter(
            fromUnit,
            conversionType,
            emissionsBySubScope,
            flattenedEmissionBySubScope,
            showChangeWhenData
          )
        : null,
      emissionsByFuelType: emissionsByFuelType
        ? convertDataForCarbonUtilityScopeOneSavingsMeter(
            fromUnit,
            conversionType,
            emissionsByFuelType,
            flattenedEmissionBySubScope,
            showChangeWhenData
          )
        : null,
      combustionType: combustionType,
      scope,
      actualConsumption: totalActualEmission,
      baselineConsumption: totalPreviousPeriodEmission,

      change: hasBaseline ? null : totalDeviation,
      actualUnit: getMaxConvertedValueFromArray(
        ConversionType.CARBON,
        fromUnit,
        [totalActualEmission]
      ).Unit,
      baselineUnit: getMaxConvertedValueFromArray(
        ConversionType.CARBON,
        fromUnit,
        [totalPreviousPeriodEmission]
      ).Unit,
      changeUnit: getMaxConvertedValueFromArray(
        ConversionType.CARBON,
        fromUnit,
        [totalDeviation]
      ).Unit,
      deviationUnit: getMaxConvertedValueFromArray(
        ConversionType.CARBON,
        fromUnit,
        [totalDeviation]
      ).Unit,
      percentageConsumptionSaving: parseFloat(
        totalDeviationPercentage.toFixed(KPI_PERCENTAGE_DECIMAL_PRECISION)
      )
      // utilityType: utilityType
    };
    meterSavingsData.push(meterSavings);
  });

  let maxRelativeValue: number | null = Number.MIN_VALUE;
  meterSavingsData?.forEach((meter: IFuelTypeSavingsData) => {
    const tempMax =
      meter.actualConsumption! > meter.baselineConsumption!
        ? meter.actualConsumption
        : meter.baselineConsumption;
    maxRelativeValue =
      tempMax! > maxRelativeValue! ? tempMax : maxRelativeValue;
  });
  meterSavingsData = meterSavingsData.map((meterdata: IFuelTypeSavingsData) => {
    return {
      ...meterdata,
      progessBarRelativeWidth: maxRelativeValue
    };
  });
  return { values: meterSavingsData, displayUnit: maxValueUnitConversion.Unit };
};

export const convertDataForCarbonUtilitySavingsSite = (
  breakdownBySite: Array<ISite> | undefined,
  fromUnit: string,
  showChangeWhenData: boolean = false
) => {
  let siteSavingsData: Array<ISiteSavingsData> = [];

  const valuesArray = getCarbonUtilitySavingsValue(breakdownBySite || []);
  const maxValueUnitConversion = getMaxConvertedValueFromArray(
    ConversionType.CARBON,
    fromUnit,
    valuesArray
  );

  breakdownBySite?.forEach((data) => {
    const {
      siteName,
      siteId,
      totalActualEmission,
      totalPreviousPeriodEmission,
      totalDeviation,
      totalDeviationPercentage,
      isCompleted
    } = data;
    const changePercent = parseFloat(
      totalDeviationPercentage.toFixed(KPI_PERCENTAGE_DECIMAL_PRECISION)
    );
    let meterSavings: ISiteSavingsData = {
      site: siteName,
      siteId,
      actualConsumption: totalActualEmission,
      baselineConsumption: totalPreviousPeriodEmission,
      change:
        showChangeWhenData &&
        (isNaN(Number(totalPreviousPeriodEmission)) ||
          isNaN(Number(totalActualEmission)) ||
          Number(totalPreviousPeriodEmission) === 0 ||
          Number(totalActualEmission) === 0 ||
          !isCompleted ||
          Math.abs(Number(changePercent)) > PERCENT_CHANGE_THRESHOLD)
          ? null
          : totalDeviation,
      percentageConsumptionSaving: parseFloat(
        totalDeviationPercentage.toFixed(KPI_PERCENTAGE_DECIMAL_PRECISION)
      ),
      progressBarActualConsumption: getConvertedValue(
        ConversionType.CARBON,
        fromUnit,
        totalActualEmission,
        maxValueUnitConversion.Unit
      ).Value,
      progressBarBaselineConsumption: getConvertedValue(
        ConversionType.CARBON,
        fromUnit,
        totalPreviousPeriodEmission,
        maxValueUnitConversion.Unit
      ).Value,
      unit: getMaxConvertedValueFromArray(ConversionType.CARBON, fromUnit, [
        totalActualEmission,
        totalPreviousPeriodEmission
      ]).Unit,
      actualUnit: getMaxConvertedValueFromArray(
        ConversionType.CARBON,
        fromUnit,
        [totalActualEmission]
      ).Unit,
      baselineUnit: getMaxConvertedValueFromArray(
        ConversionType.CARBON,
        fromUnit,
        [totalPreviousPeriodEmission]
      ).Unit,
      changeUnit: getMaxConvertedValueFromArray(
        ConversionType.CARBON,
        fromUnit,
        [totalDeviation]
      ).Unit
    };
    siteSavingsData.push(meterSavings);
  });

  let maxRelativeValue = Number.MIN_VALUE;
  siteSavingsData?.forEach((meter: ISiteSavingsData) => {
    let tempMax = meter.progressBarBaselineConsumption;
    if (
      (meter.progressBarActualConsumption || 0) >
      (meter.progressBarBaselineConsumption || 0)
    ) {
      tempMax = meter.progressBarActualConsumption;
    }
    if ((tempMax || 0) > maxRelativeValue) {
      maxRelativeValue = tempMax || 0;
    }
  });
  siteSavingsData = siteSavingsData.map((meterdata: ISiteSavingsData) => {
    return {
      progessBarRelativeWidth: maxRelativeValue,
      ...meterdata
    };
  });
  return { values: siteSavingsData, displayUnit: maxValueUnitConversion.Unit };
};

export const getSavingsBadgeColor = (cellData: number) => {
  if (cellData > 0) {
    return '#7eb338';
  } else if (cellData < 0) {
    return '#f15a4f';
  } else return '#b0b0b0';
};

export const apiCallAtCheck = (apiCallAt: ECartAt | undefined) => {
  if (apiCallAt === ECartAt.siteLabel) {
    return 'score-category-meterdata-meter';
  } else {
    return 'score-category-meterdata-meter portfolio-navigation-link';
  }
};

export const getCarbonEndDateBasedOnSub = (
  subscriptionType: string,
  endDate: string,
  startDate: string,
  dataSourceType?: any
) => {
  const userDate = new Date(endDate);
  const currentDate = new Date();
  if (
    subscriptionType === EmaasSubscriptionType.PREMIUM_SUBSCRIPTION &&
    (dataSourceType === 'livemeter' || dataSourceType === 'utilityinterval')
  ) {
    return moment(new Date(endDate)).format('YYYY-MM-DD');
  } else {
    if (
      userDate.getMonth() === currentDate.getMonth() &&
      userDate.getFullYear() === currentDate.getFullYear()
    ) {
      return formattedDate(excludeCurrentMonth(endDate, startDate));
    }
    return moment(new Date(endDate)).format('YYYY-MM-DD');
  }
};

export const getCarbonVDEndDateBasedOnSub = (
  subscriptionType: string,
  endDate: string,
  startDate: string,
  dataSourceType?: string
) => {
  const userDate = new Date(endDate);
  const currentDate = new Date();
  if (
    subscriptionType === EmaasSubscriptionType.PREMIUM_SUBSCRIPTION &&
    (dataSourceType === 'livemeter' || dataSourceType === 'utilityinterval')
  ) {
    return moment(new Date(endDate)).format('YYYY-MM-DD');
  } else {
    if (
      userDate.getMonth() === currentDate.getMonth() &&
      userDate.getFullYear() === currentDate.getFullYear()
    ) {
      return formattedDate(excludeCurrentMonth(endDate, startDate));
    }
    return moment(new Date(endDate)).format('YYYY-MM-DD');
  }
};
